// External Imports
import { h } from 'preact';
import {
  Card,
  FlagDeu,
  FlagFra,
  FlagIta,
  Inline,
  ShortText,
  Stack,
  Tag,
  Title,
  pxToRem,
  styled,
} from '@lessonnine/design-system.lib';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

// Internal Imports
import { CombinedSpanishesFlag } from './CombinedSpanishesFlag';
import { pxToRemParse } from '../../utils/styles';

// Local Variables
const languageToFlagComponent = {
  French: FlagFra,
  German: FlagDeu,
  Italian: FlagIta,
  Spanish: CombinedSpanishesFlag,
};
const minCardSize = 300;
const maxCardSize = 400;
const CardWithoutPadding = styled(Card)`
  margin: 0 auto;
  overflow: hidden;
  /* In theory, we should strive to avoid overwriting this type of CSS properties from the DS, but so far DS Cards aren't flexible enough for this kind of scenario.
     This was discussed with the DS team and they will see what to do once more usage patterns are discovered, so for now this is ok but we should keep an eye for DS updates around the topic in the future.
  */
  padding: 0;
  max-width: clamp(${pxToRem(minCardSize)}, 90vw, ${pxToRem(maxCardSize)});
  width: 100%;
  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    max-width: ${pxToRem(maxCardSize)};
  }
`;

const IllustrationContainer = styled(Stack)`
  background-size: auto min(100%, ${pxToRem(300)});
  background-position: top right;
  background-repeat: no-repeat;
  background-image: url(${({ illustration }) => illustration.cutout});
  background-color: ${({ illustration }) => illustration.backgroundColor};
  min-height: ${pxToRem(255)};
  position: relative;
  overflow: hidden;
`;

const TeacherPicture = styled.img`
  border-radius: 50%;
  width: ${pxToRem(63)};
  height: ${pxToRem(63)};
`;

// Component Definition
function ClassCard({
  cta,
  illustration,
  language,
  level,
  nonTranslatedLanguage,
  teacher,
  title,
  textColor,
}) {
  const Flag = languageToFlagComponent[nonTranslatedLanguage];

  return (
    <CardWithoutPadding color="lightened">
      <Stack style={{ flexGrow: 1 }}>
        <Stack>
          <IllustrationContainer
            gap={21}
            illustration={illustration}
            padding={[pxToRem(32), pxToRem(32)]}
          >
            <Inline>
              <Tag>{level}</Tag>
            </Inline>
            <Title as="span" color={textColor}>
              {title}
            </Title>
          </IllustrationContainer>
        </Stack>
        <Stack gap={pxToRemParse(tokens.space.x8)} padding={pxToRemParse(tokens.space.x8)}>
          <Inline alignItems="center" gap={pxToRemParse(tokens.space.x3)}>
            <TeacherPicture src={teacher.picture} />
            <Stack gap={pxToRemParse(tokens.space.x2)}>
              <Inline alignItems="center" gap={pxToRemParse(tokens.space.x2)}>
                <Flag height="19.2" width="19.2" />
                <ShortText weight="strong">{language}</ShortText>
              </Inline>
              <ShortText>{teacher.name}</ShortText>
            </Stack>
          </Inline>
          {cta}
        </Stack>
      </Stack>
    </CardWithoutPadding>
  );
}

// Module Exports
export { ClassCard, maxCardSize };
